import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Button,
  Input,
  Label,
  Spinner,
  Table,
  FormGroup,
  Row,
  Col,
} from "reactstrap";
import {
  API_TIMEOUT,
  DINAS_BY_NAMA,
  DINAS_PERANGKAT_DAERAH,
  DTSEN_BY_NIK,
  LAPORAN_BELANJA_MAMIN,
} from "../../utils";
import {
  BULAN_OPTIONS,
  PERAN_PENGELOLA,
  emptyPengelolaRow,
} from "./laporanBulananConstants";
import s from "./belanjaMamin.module.scss";

const LaporanBulananFormPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [listDinas, setListDinas] = useState([]);
  const [nikLoadingIdx, setNikLoadingIdx] = useState(-1);

  const [idPd, setIdPd] = useState("");
  const [tahun, setTahun] = useState(String(currentYear));
  const [bulan, setBulan] = useState(String(currentMonth));
  const [jenisPengelola, setJenisPengelola] = useState("Individu");
  const [namaPengelola, setNamaPengelola] = useState("");
  const [namaKelompok, setNamaKelompok] = useState("");
  const [totalBelanja, setTotalBelanja] = useState("");
  const [uraianKegiatan, setUraianKegiatan] = useState("");
  const [jumlahPaket, setJumlahPaket] = useState("");
  const [keterangan, setKeterangan] = useState("");
  const [pengelola, setPengelola] = useState([
    emptyPengelolaRow("Pengelola Utama"),
  ]);

  const isSuperAdmin = useMemo(
    () => String(user?.peran || user?.status || "").toLowerCase() === "super admin",
    [user]
  );

  const listTahun = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2018; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
    axios
      .get(DINAS_PERANGKAT_DAERAH, { timeout: API_TIMEOUT })
      .then((res) => setListDinas(res.data?.data || []))
      .catch(() => setListDinas([]));
  }, []);

  useEffect(() => {
    if (isEdit || !user || isSuperAdmin || idPd) return;
    const namaDinas = String(user.dinas || "").trim();
    if (!namaDinas) return;
    axios
      .get(DINAS_BY_NAMA, { timeout: API_TIMEOUT, params: { nama: namaDinas } })
      .then((res) => {
        if (res.data?.data?.dinas_id) setIdPd(String(res.data.data.dinas_id));
      })
      .catch(() => {
        const match = listDinas.find(
          (d) =>
            String(d.nama_dinas || "").trim().toUpperCase() ===
            namaDinas.toUpperCase()
        );
        if (match) setIdPd(String(match.dinas_id));
      });
  }, [user, isSuperAdmin, isEdit, idPd, listDinas]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axios
      .get(`${LAPORAN_BELANJA_MAMIN}/${id}`, { timeout: API_TIMEOUT })
      .then((res) => {
        const lap = res.data?.data?.laporan;
        const peng = res.data?.data?.pengelola || [];
        if (!lap) return;
        setIdPd(String(lap.id_perangkat_daerah));
        setTahun(String(lap.tahun));
        setBulan(String(lap.bulan));
        setJenisPengelola(lap.jenis_pengelola || "Individu");
        setNamaPengelola(lap.nama_pengelola || "");
        setNamaKelompok(lap.nama_kelompok || "");
        setTotalBelanja(String(lap.total_belanja_bulanan ?? ""));
        setUraianKegiatan(lap.uraian_kegiatan || "");
        setJumlahPaket(String(lap.jumlah_paket ?? ""));
        setKeterangan(lap.keterangan || "");
        setPengelola(
          peng.length > 0
            ? peng.map((p) => ({
                NIK: p.NIK || "",
                nama: p.nama || "",
                noKK: p.noKK || "",
                peran: p.peran || "Anggota",
                desa: p.desa || "",
                kecamatan: p.kecamatan || "",
                desilKesejahteraan:
                  p.desilKesejahteraan != null ? String(p.desilKesejahteraan) : "",
                catatan_verifikasi: p.catatan_verifikasi || "",
              }))
            : [emptyPengelolaRow("Pengelola Utama")]
        );
      })
      .catch(() => swal("Gagal", "Laporan tidak ditemukan", "error"))
      .finally(() => setLoading(false));
  }, [isEdit, id]);

  useEffect(() => {
    if (jenisPengelola === "Individu" && pengelola.length !== 1) {
      setPengelola([emptyPengelolaRow("Pengelola Utama")]);
    }
  }, [jenisPengelola]);

  const lookupNik = async (idx) => {
    const nik = String(pengelola[idx]?.NIK || "").trim();
    if (nik.length !== 16 || !/^\d{16}$/.test(nik)) return;
    setNikLoadingIdx(idx);
    try {
      const res = await axios.get(DTSEN_BY_NIK + encodeURIComponent(nik), {
        timeout: API_TIMEOUT,
      });
      const d = res.data?.data;
      if (!d) return;
      setPengelola((prev) => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          NIK: d.NIK || nik,
          nama: d.nama || "",
          noKK: d.noKK || "",
          desa: d.desa || "",
          kecamatan: d.kecamatan || "",
          desilKesejahteraan:
            d.desilKesejahteraan != null ? String(d.desilKesejahteraan) : "",
        };
        return next;
      });
      if (jenisPengelola === "Individu" && !namaPengelola) {
        setNamaPengelola(d.nama || "");
      }
    } catch (err) {
      swal(
        "NIK tidak ditemukan",
        err.response?.data?.message || "Data tidak ada di DTSEN",
        "warning"
      );
    } finally {
      setNikLoadingIdx(-1);
    }
  };

  const updatePengelola = (idx, field, value) => {
    setPengelola((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addAnggota = () => {
    if (jenisPengelola !== "Kelompok") return;
    setPengelola((prev) => [...prev, emptyPengelolaRow("Anggota")]);
  };

  const removePengelola = (idx) => {
    if (pengelola.length <= 1) return;
    setPengelola((prev) => prev.filter((_, i) => i !== idx));
  };

  const validateForm = () => {
    const idPdNum = parseInt(idPd, 10);
    const th = parseInt(tahun, 10);
    const bl = parseInt(bulan, 10);
    const total = parseFloat(String(totalBelanja).replace(/,/g, "."));
    if (!Number.isFinite(idPdNum)) {
      console.log("idPdNum", idPdNum);
      swal("Error", "Pilih perangkat daerah", "error");
      return false;
    }
    if (!Number.isFinite(th) || !Number.isFinite(bl) || bl < 1 || bl > 12) {
      swal("Error", "Tahun dan bulan wajib diisi dengan benar", "error");
      return false;
    }
    if (!Number.isFinite(total) || total < 0) {
      swal("Error", "Total belanja bulanan wajib diisi", "error");
      return false;
    }
    const validPeng = pengelola.filter((p) => String(p.NIK || "").trim().length === 16);
    if (validPeng.length === 0) {
      swal("Error", "Minimal satu NIK pengelola wajib (16 digit)", "error");
      return false;
    }
    if (jenisPengelola === "Individu" && validPeng.length !== 1) {
      swal("Error", "Pengelola individu hanya satu NIK", "error");
      return false;
    }
    const nikSet = new Set();
    for (const p of validPeng) {
      const n = String(p.NIK).trim();
      if (nikSet.has(n)) {
        swal("Error", "NIK pengelola tidak boleh duplikat dalam satu laporan", "error");
        return false;
      }
      nikSet.add(n);
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    const payload = {
      id_perangkat_daerah: parseInt(idPd, 10),
      tahun: parseInt(tahun, 10),
      bulan: parseInt(bulan, 10),
      jenis_pengelola: jenisPengelola,
      nama_pengelola: namaPengelola || null,
      nama_kelompok: jenisPengelola === "Kelompok" ? namaKelompok || null : null,
      total_belanja_bulanan: parseFloat(String(totalBelanja).replace(/,/g, ".")),
      uraian_kegiatan: uraianKegiatan || null,
      jumlah_paket: parseInt(jumlahPaket, 10) || 0,
      keterangan: keterangan || null,
      createdBy: user?.id || null,
      pengelola: pengelola
        .filter((p) => String(p.NIK || "").trim().length === 16)
        .map((p) => ({
          NIK: String(p.NIK).trim(),
          nama: p.nama || null,
          noKK: p.noKK || null,
          peran: p.peran || "Anggota",
          desa: p.desa || null,
          kecamatan: p.kecamatan || null,
          desilKesejahteraan:
            p.desilKesejahteraan !== "" ? parseInt(p.desilKesejahteraan, 10) : null,
          catatan_verifikasi: p.catatan_verifikasi || null,
        })),
    };
    try {
      if (isEdit) {
        await axios.patch(`${LAPORAN_BELANJA_MAMIN}/${id}`, payload, {
          timeout: API_TIMEOUT,
        });
      } else {
        await axios.post(LAPORAN_BELANJA_MAMIN, payload, { timeout: API_TIMEOUT });
      }
      if (isEdit) {
        swal("Sukses", "Laporan berhasil disimpan", "success");
        history.push("/admin/makan-minum/laporan-bulanan");
        return;
      }
      const buatLagi = await swal({
        title: "Laporan tersimpan",
        text: "Buat laporan lain untuk bulan dan tahun yang sama?",
        icon: "success",
        buttons: ["Ke daftar laporan", "Buat laporan lagi"],
      });
      if (buatLagi) {
        setJenisPengelola("Individu");
        setNamaPengelola("");
        setNamaKelompok("");
        setTotalBelanja("");
        setUraianKegiatan("");
        setJumlahPaket("");
        setKeterangan("");
        setPengelola([emptyPengelolaRow("Pengelola Utama")]);
        return;
      }
      history.push("/admin/makan-minum/laporan-bulanan");
    } catch (err) {
      swal(
        "Gagal",
        err.response?.data?.message || err.message || "Gagal menyimpan",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>{isEdit ? "Ubah" : "Buat"} Laporan Bulanan</h2>
        <p>
          Input laporan belanja makan-minum per kegiatan. Perangkat daerah dapat
          mengirim beberapa laporan terpisah untuk bulan dan tahun yang sama;
          setiap laporan disimpan satu per satu.
        </p>
      </div>

      <div className="mb-3">
        <Button
          tag={Link}
          to="/admin/makan-minum/laporan-bulanan"
          color="link"
          className="p-0"
        >
          ← Kembali ke daftar laporan
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className={s.sectionCard}>
          <div className={s.cardHeader}>Data laporan</div>
          <div className={s.cardBody}>
            <Row>
              <Col md={isSuperAdmin ? 6 : 12}>
                <FormGroup>
                  <Label>Perangkat daerah</Label>
                  {isSuperAdmin ? (
                    <Input
                      type="select"
                      value={idPd}
                      onChange={(e) => setIdPd(e.target.value)}
                      required
                      disabled={isEdit}
                    >
                      <option value="">— Pilih —</option>
                      {listDinas.map((d) => (
                        <option key={d.dinas_id} value={String(d.dinas_id)}>
                          {d.nama_dinas}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <Input
                      plaintext
                      readOnly
                      value={
                        listDinas.find((d) => String(d.dinas_id) === idPd)
                          ?.nama_dinas ||
                        user?.dinas ||
                        "—"
                      }
                    />
                  )}
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Tahun</Label>
                  <Input
                    type="select"
                    value={tahun}
                    onChange={(e) => setTahun(e.target.value)}
                    disabled={isEdit}
                    required
                  >
                    {listTahun.map((y) => (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Bulan</Label>
                  <Input
                    type="select"
                    value={bulan}
                    onChange={(e) => setBulan(e.target.value)}
                    disabled={isEdit}
                    required
                  >
                    {BULAN_OPTIONS.map((b) => (
                      <option key={b.value} value={String(b.value)}>
                        {b.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            {isEdit && (
              <p className="small text-muted">
                Tahun, bulan, dan perangkat daerah tidak dapat diubah saat
                mengedit. Buat laporan baru jika perlu entri tambahan untuk
                periode yang sama.
              </p>
            )}

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Jenis pengelola</Label>
                  <Input
                    type="select"
                    value={jenisPengelola}
                    onChange={(e) => setJenisPengelola(e.target.value)}
                  >
                    <option value="Individu">Individu</option>
                    <option value="Kelompok">Kelompok</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Nama pengelola</Label>
                  <Input
                    value={namaPengelola}
                    onChange={(e) => setNamaPengelola(e.target.value)}
                    placeholder="Nama pengelola utama"
                  />
                </FormGroup>
              </Col>
              {jenisPengelola === "Kelompok" && (
                <Col md={4}>
                  <FormGroup>
                    <Label>Nama kelompok</Label>
                    <Input
                      value={namaKelompok}
                      onChange={(e) => setNamaKelompok(e.target.value)}
                    />
                  </FormGroup>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Total belanja bulanan (Rp)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={totalBelanja}
                    onChange={(e) => setTotalBelanja(e.target.value)}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Jumlah paket</Label>
                  <Input
                    type="number"
                    min="0"
                    value={jumlahPaket}
                    onChange={(e) => setJumlahPaket(e.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Uraian kegiatan</Label>
              <Input
                type="textarea"
                rows={2}
                value={uraianKegiatan}
                onChange={(e) => setUraianKegiatan(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Keterangan</Label>
              <Input
                type="textarea"
                rows={2}
                value={keterangan}
                onChange={(e) => setKeterangan(e.target.value)}
              />
            </FormGroup>
          </div>
        </div>

        <div className={s.sectionCard}>
          <div className={s.cardHeader}>
            Pengelola (sumber DTSEN)
            {jenisPengelola === "Kelompok" && (
              <Button
                type="button"
                color="secondary"
                size="sm"
                className="float-end"
                onClick={addAnggota}
              >
                + Anggota
              </Button>
            )}
          </div>
          <div className={`${s.cardBody} ${s.tableWrap}`}>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>NIK</th>
                  <th>Nama</th>
                  <th>No KK</th>
                  <th>Desa</th>
                  <th>Kecamatan</th>
                  <th>Desil</th>
                  <th>Peran</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {pengelola.map((p, idx) => (
                  <tr key={idx}>
                    <td style={{ minWidth: 160 }}>
                      <Input
                        bsSize="sm"
                        value={p.NIK}
                        onChange={(e) => updatePengelola(idx, "NIK", e.target.value)}
                        onBlur={() => lookupNik(idx)}
                        placeholder="16 digit"
                        maxLength={16}
                      />
                      {nikLoadingIdx === idx && (
                        <Spinner size="sm" className="mt-1" />
                      )}
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        value={p.nama}
                        readOnly
                        className="bg-light"
                      />
                    </td>
                    <td>
                      <Input bsSize="sm" value={p.noKK} readOnly className="bg-light" />
                    </td>
                    <td>
                      <Input bsSize="sm" value={p.desa} readOnly className="bg-light" />
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        value={p.kecamatan}
                        readOnly
                        className="bg-light"
                      />
                    </td>
                    <td style={{ width: 56 }}>
                      <Input
                        bsSize="sm"
                        value={p.desilKesejahteraan}
                        readOnly
                        className="bg-light text-center"
                      />
                    </td>
                    <td style={{ minWidth: 130 }}>
                      <Input
                        bsSize="sm"
                        type="select"
                        value={p.peran}
                        onChange={(e) => updatePengelola(idx, "peran", e.target.value)}
                        disabled={jenisPengelola === "Individu"}
                      >
                        {PERAN_PENGELOLA.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td>
                      {jenisPengelola === "Kelompok" && pengelola.length > 1 && (
                        <Button
                          type="button"
                          color="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => removePengelola(idx)}
                        >
                          ×
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="small text-muted mb-0">
              Ketik NIK lalu keluar dari kolom (blur) untuk mengambil data dari
              DTSEN. Beberapa laporan boleh untuk bulan yang sama; NIK tidak
              boleh duplikat dalam satu laporan.
            </p>
          </div>
        </div>

        <div className="mt-3">
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Simpan laporan"}
          </Button>{" "}
          <Button tag={Link} to="/admin/makan-minum/laporan-bulanan" color="secondary" outline>
            Batal
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LaporanBulananFormPage;
